// État de session partagé : chargé depuis /api/v1/auth/etat, applique les préférences du serveur.

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ErreurApi, surSessionPerdue } from '../api/client';
import { etatSessionHorsLigne, memoriserEtatSession, oublierSessionLocale } from '../horsligne/contexte';
import type { EtatSession } from '../api/types';
import { appliquerDensite, appliquerTaille, appliquerTheme } from './theme';

interface ValeurSession {
  etat: EtatSession | undefined;
  chargement: boolean;
  erreur: unknown;
  recharger: () => Promise<void>;
  deconnecter: () => Promise<void>;
}

const ContexteSession = createContext<ValeurSession | null>(null);

export const CLE_ETAT = ['auth', 'etat'] as const;

export function FournisseurSession({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const requete = useQuery({
    queryKey: CLE_ETAT,
    // Hors connexion (aucune réponse du serveur), la dernière session confirmée rouvre l'application pendant 24 h
    // (cadrage, section 9) ; au-delà, ou après déconnexion, le serveur est indispensable.
    queryFn: async () => {
      try {
        const etat = (await api<EtatSession>('/api/v1/auth/etat')).donnees;
        if (etat.connecte) void memoriserEtatSession(etat);
        return etat;
      } catch (e) {
        if (e instanceof ErreurApi && e.statut === 0) {
          const local = await etatSessionHorsLigne();
          if (local) return local;
        }
        throw e;
      }
    },
    staleTime: 60_000,
    retry: 1,
  });

  // Préférences persistées côté serveur : elles priment sur le cache local à la connexion.
  const preferences = requete.data?.preferences;
  useEffect(() => {
    if (preferences) {
      appliquerTheme(preferences.theme);
      appliquerDensite(preferences.densite);
      appliquerTaille(preferences.tailleTexte ?? 'normale');
    }
  }, [preferences]);

  useEffect(
    () =>
      surSessionPerdue(() => {
        void client.invalidateQueries({ queryKey: CLE_ETAT });
      }),
    [client],
  );

  const valeur: ValeurSession = {
    etat: requete.data,
    chargement: requete.isLoading,
    erreur: requete.error,
    recharger: async () => {
      await client.invalidateQueries({ queryKey: CLE_ETAT });
    },
    deconnecter: async () => {
      try {
        await api('/api/v1/auth/deconnexion', { methode: 'POST', corps: {} });
      } finally {
        await oublierSessionLocale();
        // Les données métier en cache sont oubliées ; l'état de session est relu (retour à la connexion).
        client.removeQueries({ predicate: (q) => q.queryKey[0] !== CLE_ETAT[0] });
        await client.refetchQueries({ queryKey: CLE_ETAT });
      }
    },
  };
  return <ContexteSession.Provider value={valeur}>{children}</ContexteSession.Provider>;
}

export function useSession(): ValeurSession {
  const v = useContext(ContexteSession);
  if (!v) throw new Error('useSession hors du fournisseur de session');
  return v;
}

/** Vrai si l'utilisateur dispose d'une affectation dont le rôle est l'un de ceux donnés. */
export function aUnRole(etat: EtatSession | undefined, ...roles: string[]): boolean {
  return Boolean(etat?.roles?.some((r) => roles.includes(r)));
}
